import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Input";
import {
  SdTestAppBenchClient,
  type BenchLiveStepTreeState,
  type BenchSequenceRow,
  type BenchStepTreeEntry,
} from "@/lib/sdtestapp/sdTestAppBenchClient";
import type { SdTestDevice } from "@/lib/sdtestapp/sdTestAppHttpClient";
import type { ProductionSlotsState } from "@/lib/sdtestapp/sdTestAppProductionClient";

import { BenchBatchCustomSequencesModal } from "./BenchBatchCustomSequencesModal";
import { BenchBatchQcIncidentModal } from "./BenchBatchQcIncidentModal";
import { BenchBatchStepTree } from "./BenchBatchStepTree";
import { assignBenchRackSlotWithSwap } from "./benchRackSlotAssign";
import { BenchRackCanvas } from "./BenchRackCanvas";

const MANUAL_SET_NAME = "Manual Set";
const CUSTOM_SET_NAME = "Custom Set";

export function BenchBatchTestsTab({
  port,
  devices,
  canActuate,
  selectedConnectorId,
  onSelectConnector,
}: {
  port: string;
  devices: SdTestDevice[];
  canActuate: boolean;
  selectedConnectorId: string | null;
  onSelectConnector: (connectorId: string) => void;
}): JSX.Element {
  const client = useMemo(() => new SdTestAppBenchClient(port), [port]);
  const [sequences, setSequences] = useState<BenchSequenceRow[]>([]);
  const [sequenceSet, setSequenceSet] = useState("Production");
  const [sequenceId, setSequenceId] = useState("");
  const [slotsState, setSlotsState] = useState<ProductionSlotsState | null>(null);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchPhase, setBatchPhase] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resyncMessage, setResyncMessage] = useState<string | null>(null);
  const [stepTreeEntries, setStepTreeEntries] = useState<BenchStepTreeEntry[]>([]);
  const [liveStepTree, setLiveStepTree] = useState<BenchLiveStepTreeState | null>(null);
  const [qcIncidentDevice, setQcIncidentDevice] = useState<SdTestDevice | null>(null);
  const [qcIncidentMessage, setQcIncidentMessage] = useState<string | null>(null);
  const [manualSequence, setManualSequence] = useState("");
  const [configureModel, setConfigureModel] = useState("");
  const [configureMessage, setConfigureMessage] = useState<string | null>(null);
  const [customModalOpen, setCustomModalOpen] = useState(false);

  const sequenceSets = useMemo(() => {
    const sets = new Set(sequences.map((row) => row.setName));
    const ordered: string[] = [];
    if (sets.has(MANUAL_SET_NAME)) ordered.push(MANUAL_SET_NAME);
    if (sets.has(CUSTOM_SET_NAME)) ordered.push(CUSTOM_SET_NAME);
    if (sets.has("Production")) ordered.push("Production");
    for (const setName of Array.from(sets).sort()) {
      if (!ordered.includes(setName)) ordered.push(setName);
    }
    return ordered;
  }, [sequences]);

  const filteredSequences = useMemo(() => {
    if (sequenceSet === CUSTOM_SET_NAME) {
      return sequences.filter((row) => row.source === "custom" || row.setName.startsWith("Custom:"));
    }
    return sequences.filter((row) => row.setName === sequenceSet);
  }, [sequences, sequenceSet]);

  const refreshAll = useCallback(async () => {
    if (!canActuate) return;
    const [nextSequences, nextSlots, batchStatus, nextLiveTree] = await Promise.all([
      client.fetchBatchSequences(),
      client.fetchSlots(),
      client.fetchBatchStatus(),
      client.fetchLiveBatchStepTree(),
    ]);
    if (batchStatus.slots && nextSlots.slots.length > 0) {
      nextSlots.slots = nextSlots.slots.map((slot) => ({
        ...slot,
        status: batchStatus.slots?.[slot.slotName]?.status ?? slot.status,
      }));
    }
    setSequences(nextSequences);
    setSlotsState(nextSlots);
    setLiveStepTree(nextLiveTree);
    setBatchRunning(Boolean(batchStatus.isRunning));
    setBatchPhase(
      batchStatus.isRunning
        ? `Step ${batchStatus.currentStep ?? "?"} / ${batchStatus.totalSteps ?? "?"}`
        : "Idle",
    );
    if (!sequenceId && nextSequences.length > 0) {
      const firstProduction = nextSequences.find((row) => row.setName === "Production");
      setSequenceId(firstProduction?.id ?? nextSequences[0]?.id ?? "");
    }
  }, [canActuate, client, sequenceId]);

  useEffect(() => {
    void refreshAll().catch(() => undefined);
    if (!canActuate) return undefined;
    const intervalId = setInterval(() => void refreshAll().catch(() => undefined), 2500);
    return () => clearInterval(intervalId);
  }, [canActuate, refreshAll]);

  useEffect(() => {
    if (!canActuate || !sequenceId) {
      setStepTreeEntries([]);
      return;
    }
    void client
      .fetchBatchStepTree(sequenceId, sequenceSet)
      .then(setStepTreeEntries)
      .catch(() => setStepTreeEntries([]));
  }, [canActuate, client, sequenceId, sequenceSet]);

  const runAction = async (action: () => Promise<void>) => {
    setError(null);
    setPending(true);
    try {
      await action();
      await refreshAll();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : String(actionError));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 rounded border p-3">
        <div>
          <Label htmlFor="bench-sequence-set">Sequence set</Label>
          <select
            id="bench-sequence-set"
            className="mt-1 block rounded border px-2 py-1 text-sm"
            value={sequenceSet}
            onChange={(event) => setSequenceSet(event.target.value)}
          >
            {sequenceSets.map((setName) => (
              <option key={setName} value={setName}>
                {setName}
              </option>
            ))}
          </select>
        </div>
        <div className="min-w-[220px]">
          <Label htmlFor="bench-sequence-id">Sequence</Label>
          {sequenceSet === MANUAL_SET_NAME ? (
            <textarea
              id="bench-sequence-id"
              className="mt-1 block w-full rounded border px-2 py-1 font-mono text-sm"
              rows={3}
              value={manualSequence}
              onChange={(event) => setManualSequence(event.target.value)}
              placeholder="Enter manual sequence steps…"
            />
          ) : (
            <select
              id="bench-sequence-id"
              className="mt-1 block w-full rounded border px-2 py-1 text-sm"
              value={sequenceId}
              onChange={(event) => setSequenceId(event.target.value)}
            >
              {filteredSequences.map((row) => (
                <option key={`${row.setName}:${row.id}`} value={row.id}>
                  {row.name} ({row.stepCount ?? 0} steps)
                </option>
              ))}
            </select>
          )}
        </div>
        {sequenceSet === CUSTOM_SET_NAME ? (
          <Button type="button" variant="secondary" disabled={!canActuate || pending} onClick={() => setCustomModalOpen(true)}>
            Manage custom sequences
          </Button>
        ) : null}
        <Button
          type="button"
          variant="secondary"
          disabled={!canActuate || pending}
          onClick={() => void runAction(async () => {
            const result = await client.resyncSequences();
            setResyncMessage(
              result.success
                ? `Resync complete (${result.skuTemplateCount ?? 0} SKU templates).`
                : "Resync completed with errors.",
            );
          })}
        >
          Resync sequences
        </Button>
        <Button
          type="button"
          disabled={
            !canActuate
            || pending
            || batchRunning
            || (sequenceSet === MANUAL_SET_NAME ? manualSequence.trim() === "" : !sequenceId)
          }
          onClick={() => void runAction(async () => {
            const assignedSlots = (slotsState?.slots ?? [])
              .filter((slot) => slot.connectorId)
              .map((slot) => slot.slotName);
            await client.startBatch({
              sequenceId: sequenceSet === MANUAL_SET_NAME ? "" : sequenceId,
              sequenceSet,
              slots: assignedSlots.length > 0 ? assignedSlots : undefined,
              manualSequence: sequenceSet === MANUAL_SET_NAME ? manualSequence.trim() : undefined,
            });
          })}
        >
          Run tests
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={!canActuate || pending || !batchRunning}
          onClick={() => void runAction(() => client.stopBatch())}
        >
          Stop
        </Button>
        <Badge tone={batchRunning ? "warning" : "muted"}>{batchPhase}</Badge>
      </div>

      {resyncMessage ? <p className="text-sm text-muted-foreground">{resyncMessage}</p> : null}
      {configureMessage ? <p className="text-sm text-muted-foreground">{configureMessage}</p> : null}

      {canActuate && (sequenceSet === MANUAL_SET_NAME || sequenceSet === CUSTOM_SET_NAME || sequenceSet === "Production") ? (
        <div className="flex flex-wrap items-end gap-3 rounded border p-3">
          <div className="min-w-[220px]">
            <Label htmlFor="bench-configure-model">Configure all model number</Label>
            <input
              id="bench-configure-model"
              className="mt-1 block w-full rounded border px-2 py-1 text-sm"
              value={configureModel}
              onChange={(event) => setConfigureModel(event.target.value.toUpperCase())}
              placeholder="e.g. ABC123"
            />
          </div>
          <Button
            type="button"
            variant="secondary"
            disabled={!canActuate || pending || configureModel.trim() === ""}
            onClick={() => void runAction(async () => {
              const result = await client.configureAllNonReference(configureModel.trim());
              setConfigureMessage(
                result.success
                  ? `Configured ${result.successCount}/${result.targetCount} devices to ${result.modelNumber}.`
                  : `Configure all: ${result.successCount}/${result.targetCount} succeeded.`,
              );
            })}
          >
            Configure all (non-reference)
          </Button>
        </div>
      ) : null}
      {qcIncidentMessage ? <p className="text-sm text-emerald-700">{qcIncidentMessage}</p> : null}

      <div className="rounded border p-3">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {liveStepTree?.live ? "Step tree (live)" : "Step preview"}
        </p>
        <BenchBatchStepTree entries={stepTreeEntries} liveState={liveStepTree} />
      </div>

      <div className="rounded border p-3">
        <div className="mb-3 flex flex-wrap gap-2">
          <Button type="button" variant="secondary" size="sm" disabled={!canActuate || pending} onClick={() => void runAction(async () => {
            const next = await client.syncSlots();
            setSlotsState(next);
          })}>
            Sync slots from engine
          </Button>
        </div>
        <BenchRackCanvas
          slots={slotsState?.slots ?? []}
          devices={devices}
          selectedConnectorId={selectedConnectorId}
          poolOnlyConnectorIds={slotsState?.poolOnlyConnectorIds ?? []}
          rackLayout={slotsState?.rackLayout}
          onSelectConnector={onSelectConnector}
          onAssignSlot={(slotName, connectorId, sourceSlotName) => {
            void runAction(async () => {
              if (!connectorId) {
                const next = await client.assignSlot(slotName, null);
                setSlotsState(next);
                return;
              }
              const next = await assignBenchRackSlotWithSwap(
                client,
                slotsState?.slots ?? [],
                slotName,
                connectorId,
                sourceSlotName,
              );
              setSlotsState(next);
            });
          }}
          onLogQcIncident={
            canActuate
              ? (connectorId) => {
                  const device = devices.find((row) => String(row.connectorId ?? "") === connectorId) ?? null;
                  setQcIncidentDevice(device);
                }
              : undefined
          }
        />
      </div>

      <BenchBatchQcIncidentModal
        open={qcIncidentDevice !== null}
        device={qcIncidentDevice}
        onClose={() => setQcIncidentDevice(null)}
        onSubmitted={(incidentId) => {
          setQcIncidentMessage(`QC incident #${incidentId} filed.`);
        }}
      />

      <BenchBatchCustomSequencesModal
        open={customModalOpen}
        port={port}
        onClose={() => setCustomModalOpen(false)}
        onChanged={() => void refreshAll().catch(() => undefined)}
      />

      {pending ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Working…
        </p>
      ) : null}
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {!canActuate ? (
        <p className="text-sm text-muted-foreground">Batch Tests require local SDTestApp actuation on this bench PC.</p>
      ) : null}
    </div>
  );
}
